/**
 * Jester Agent chat via MiniApp MCP routes.
 * Auth: terminal session cookie (same as portfolio) — `requireTelegramAuth` accepts it.
 *
 * LLM provider keys live on `/api/terminal/account/llm-keys` (Grail SQL today)
 * so Console and Oroboros share one API. Mini App MCP provider routes remain
 * aliases until Valet has a vault and the old path can be retired.
 *
 * Terminal-needed surface only: stream chat, provider keys, mutate-confirm round-trip,
 * chart_snapshot payloads for inline Agent charts, and conversation share/import.
 * Not ported: consensus drawers, trade dock.
 */

import { oroborosFetch, oroborosJson, oroborosAuthHeaders } from './client';
import { interpretOroMutateApiResponse } from './tradeResponseQueue';

export type AgentChatRole = 'user' | 'assistant' | 'system';

export type AgentChatMessage = {
  role: AgentChatRole;
  content: string;
};

export type AgentProviderKey = {
  id: string;
  label?: string;
  model?: string;
  providerId?: string;
  maskedKey?: string;
  lastTestOk?: boolean | null;
  baseUrl?: string;
};

export type AgentProviderDef = {
  id: string;
  label: string;
  keyHint?: string;
  defaultModel?: string;
  baseUrl?: string;
  models?: Array<{ id: string; label: string }>;
  supportsCustomModel?: boolean;
  requiresBaseUrl?: boolean;
};

export type AgentPendingMutateConfirm = {
  toolName: string;
  args: Record<string, unknown>;
  confirmToken: string;
  summary?: string;
  storedAt: number;
};

export type AgentChatCompleteResult = {
  success: boolean;
  content?: string;
  error?: string;
  toolCallCount?: number;
};

export type AgentToolResultEvent = {
  name: string;
  ok: boolean;
  requiresConfirmation?: boolean;
  confirmToken?: string;
  confirmArgs?: Record<string, unknown>;
  summary?: string;
  userMessage?: string;
  /** Full tool payload (e.g. `jester_chart_snapshot.chart` for inline Agent chart). */
  result?: Record<string, unknown>;
};

export type AgentChatStreamHandlers = {
  onStatus?: (message: string) => void;
  onDelta?: (delta: string) => void;
  onToolResult?: (event: AgentToolResultEvent) => void;
  onDone?: (content: string) => void;
  onError?: (message: string) => void;
  signal?: AbortSignal;
};

const CHAT_TIMEOUT_MS = 5 * 60 * 1000;
const HISTORY_TURN_CAP = 24;
export const AGENT_PENDING_MUTATE_TTL_MS = 5 * 60 * 1000;

function apiMessages(messages: AgentChatMessage[]): Array<{ role: string; content: string }> {
  return messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .slice(-HISTORY_TURN_CAP)
    .map((m) => ({ role: m.role, content: String(m.content || '').trim() }))
    .filter((m) => m.content);
}

function pendingForApi(
  pending?: AgentPendingMutateConfirm | null,
): { toolName: string; args: Record<string, unknown>; confirmToken: string; summary?: string } | null {
  if (!pending?.confirmToken?.trim() || !pending.toolName?.trim()) return null;
  if (Date.now() - pending.storedAt > AGENT_PENDING_MUTATE_TTL_MS) return null;
  return {
    toolName: pending.toolName,
    args: pending.args || {},
    confirmToken: pending.confirmToken,
    summary: pending.summary,
  };
}

function chatBody(
  messages: AgentChatMessage[],
  opts?: { keyId?: string; pendingMutateConfirm?: AgentPendingMutateConfirm | null },
) {
  const pending = pendingForApi(opts?.pendingMutateConfirm);
  return {
    keyId: opts?.keyId || undefined,
    messages: apiMessages(messages),
    ...(pending ? { pendingMutateConfirm: pending } : {}),
  };
}

const AUTH_VAULT_PROXY_ERROR =
  /proxy to https?:\/\/auth\.artifice\.systems failed/i;

function readAgentErrorMessage(data: unknown, fallback = ''): string {
  if (!data || typeof data !== 'object') return fallback;
  const row = data as { error?: unknown; message?: unknown };
  return String(row.error || row.message || fallback);
}

function isAuthVaultProxyError(raw: string | undefined | null): boolean {
  return AUTH_VAULT_PROXY_ERROR.test(String(raw || ''));
}

/** MiniApp ADMIN_RIG gate copy — rewrite for Oroboros so it doesn't look like a bot redirect glitch. */
export function humanizeAgentApiError(raw: string | undefined | null): string {
  const msg = String(raw || '').trim();
  if (!msg) return 'Request failed';
  if (/nice try,\s*but your bot is at another location/i.test(msg) || /wrong_deployment/i.test(msg)) {
    return (
      'This host is locked to allow-listed admins. Sign in with an admin account, or use a deployment that is not in admin-only mode.'
    );
  }
  if (isAuthVaultProxyError(msg) || /valet auth proxy/i.test(msg)) {
    return 'Provider list temporarily unavailable — tap Retry';
  }
  return msg;
}

const ACCOUNT_LLM_KEYS = '/api/terminal/account/llm-keys';
const MINIAPP_PROVIDERS = '/api/miniapp/mcp/providers';

function shouldFallbackToMiniappProviders(status: number, data?: unknown, error?: string): boolean {
  // 404: llm-keys not registered yet. 502/503/504 or a 200 `{ success:false }`
  // body: Valet cutover reverse-proxied `/api/terminal/account/*` to
  // auth.artifice.systems, which has no key vault.
  if (status === 404 || status === 502 || status === 503 || status === 504) return true;
  return isAuthVaultProxyError(readAgentErrorMessage(data, error || ''));
}

async function llmKeysJson<T>(
  action: 'list' | 'upsert' | 'delete' | 'test' | 'models',
  init?: RequestInit,
) {
  const primary = await oroborosJson<T>(`${ACCOUNT_LLM_KEYS}/${action}`, init);
  if (shouldFallbackToMiniappProviders(primary.status, primary.data, primary.error)) {
    return oroborosJson<T>(`${MINIAPP_PROVIDERS}/${action}`, init);
  }
  return primary;
}

type AgentProviderListResult = {
  ok: boolean;
  keys: AgentProviderKey[];
  providers: AgentProviderDef[];
  envProvider: AgentProviderKey | null;
  envProviderAllowed: boolean;
  envProviderConfigured: boolean;
  error?: string;
};

let listAgentProvidersInflight: Promise<AgentProviderListResult> | null = null;

async function listAgentProvidersOnce(): Promise<AgentProviderListResult> {
  const attempt = async () =>
    llmKeysJson<{
      success?: boolean;
      keys?: AgentProviderKey[];
      providers?: AgentProviderDef[];
      envProvider?: AgentProviderKey | null;
      envProviderAllowed?: boolean;
      envProviderConfigured?: boolean;
      error?: string;
      message?: string;
    }>('list', {
      method: 'POST',
      headers: oroborosAuthHeaders(),
      body: JSON.stringify({}),
    });

  let { ok, status, data } = await attempt();
  // Edge/proxy 502/503 while a heavy MCP chat is running — one short retry.
  if ((!ok && (status === 502 || status === 503 || status === 504)) || status === 0) {
    await new Promise((r) => setTimeout(r, 400));
    ({ ok, status, data } = await attempt());
  }

  if (!ok || data?.success === false) {
    const raw = data?.error || data?.message || '';
    const fallback =
      status === 502 || status === 503 || status === 504
        ? 'Provider list temporarily unavailable — tap Retry'
        : status === 0
          ? 'Could not reach server for provider keys'
          : `Providers failed (${status})`;
    return {
      ok: false,
      keys: [],
      providers: [],
      envProvider: null,
      envProviderAllowed: false,
      envProviderConfigured: false,
      error: humanizeAgentApiError(raw || fallback),
    };
  }
  const keys = Array.isArray(data.keys) ? data.keys.filter((k) => k?.id) : [];
  const providers = Array.isArray(data.providers)
    ? data.providers.filter((p) => p?.id && p?.label)
    : [];
  const envProvider =
    data.envProvider && typeof data.envProvider === 'object' && data.envProvider.id
      ? data.envProvider
      : null;
  return {
    ok: true,
    keys,
    providers,
    envProvider,
    envProviderAllowed: !!data.envProviderAllowed,
    envProviderConfigured: !!data.envProviderConfigured,
  };
}

export async function listAgentProviders(): Promise<AgentProviderListResult> {
  if (listAgentProvidersInflight) return listAgentProvidersInflight;
  const pending = listAgentProvidersOnce();
  listAgentProvidersInflight = pending;
  try {
    return await pending;
  } finally {
    if (listAgentProvidersInflight === pending) listAgentProvidersInflight = null;
  }
}

export async function upsertAgentProvider(opts: {
  providerId: string;
  apiKey: string;
  label?: string;
  model: string;
  baseUrl?: string;
}): Promise<{ ok: boolean; error?: string }> {
  const { ok, status, data } = await llmKeysJson<{
    success?: boolean;
    error?: string;
    message?: string;
  }>('upsert', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      providerId: opts.providerId,
      apiKey: opts.apiKey,
      label: opts.label,
      model: opts.model,
      baseUrl: opts.baseUrl || undefined,
    }),
  });
  const interpreted = interpretOroMutateApiResponse(ok, status, data, {
    rejectQueued: false,
    fallbackError: `Save failed (${status})`,
    networkError: 'Network error — provider save failed',
  });
  if (!interpreted.success) {
    return {
      ok: false,
      error: humanizeAgentApiError(interpreted.error || `Save failed (${status})`),
    };
  }
  return { ok: true };
}

export async function deleteAgentProvider(keyId: string): Promise<{ ok: boolean; error?: string }> {
  const { ok, status, data } = await llmKeysJson<{
    success?: boolean;
    error?: string;
    message?: string;
  }>('delete', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({ keyId }),
  });
  const interpreted = interpretOroMutateApiResponse(ok, status, data, {
    rejectQueued: false,
    fallbackError: `Delete failed (${status})`,
    networkError: 'Network error — provider delete failed',
  });
  if (!interpreted.success) {
    return {
      ok: false,
      error: humanizeAgentApiError(interpreted.error || `Delete failed (${status})`),
    };
  }
  return { ok: true };
}

export async function testAgentProvider(keyId: string): Promise<{ ok: boolean; message?: string; error?: string }> {
  const { ok, status, data } = await llmKeysJson<{
    success?: boolean;
    message?: string;
    error?: string;
  }>('test', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({ keyId }),
  });
  if (!ok || data?.success === false) {
    return {
      ok: false,
      error: humanizeAgentApiError(data?.error || data?.message || `Test failed (${status})`),
    };
  }
  // Connectivity probe asks the model to reply "ok" — never surface that raw echo.
  const raw = String(data?.message || '').trim();
  const message =
    !raw || /^ok\.?$/i.test(raw) || /^provider responded$/i.test(raw)
      ? 'Provider connected'
      : raw;
  return { ok: true, message };
}

export async function listAgentProviderModels(opts: {
  providerId: string;
  apiKey?: string;
  keyId?: string;
  baseUrl?: string;
  forceRefresh?: boolean;
}): Promise<{
  ok: boolean;
  models: Array<{ id: string; label: string }>;
  defaultModel?: string;
  source?: 'live' | 'static';
  message?: string;
  error?: string;
}> {
  const { ok, status, data } = await llmKeysJson<{
    success?: boolean;
    models?: Array<{ id: string; label: string }>;
    defaultModel?: string;
    source?: 'live' | 'static';
    error?: string;
    message?: string;
  }>('models', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      providerId: opts.providerId,
      apiKey: opts.apiKey || undefined,
      keyId: opts.keyId || undefined,
      baseUrl: opts.baseUrl || undefined,
      forceRefresh: opts.forceRefresh || undefined,
    }),
  });
  if (!ok || data?.success === false) {
    return {
      ok: false,
      models: [],
      error: humanizeAgentApiError(data?.error || data?.message || `Models failed (${status})`),
    };
  }
  return {
    ok: true,
    models: Array.isArray(data.models) ? data.models.filter((m) => m?.id) : [],
    defaultModel: data.defaultModel ? String(data.defaultModel) : undefined,
    source: data.source === 'live' ? 'live' : data.source === 'static' ? 'static' : undefined,
    message: String(data.message || '').trim() || undefined,
  };
}

/** Prefer env/mainframe when allowed, else first user key, else keep previous if still valid. */
export function pickAgentKeyId(
  merged: AgentProviderKey[],
  envProvider: AgentProviderKey | null,
  previous?: string,
): string {
  if (previous && merged.some((k) => k.id === previous)) return previous;
  if (envProvider?.id && merged.some((k) => k.id === envProvider.id)) return envProvider.id;
  return merged[0]?.id || '';
}

export function mergeAgentProviderKeys(
  keys: AgentProviderKey[],
  envProvider: AgentProviderKey | null,
): AgentProviderKey[] {
  if (!envProvider?.id) return keys.map((k) => ({ id: k.id, label: k.label, model: k.model }));
  return [
    { id: envProvider.id, label: envProvider.label || 'Server key', model: envProvider.model },
    ...keys
      .filter((k) => k.id !== envProvider.id)
      .map((k) => ({ id: k.id, label: k.label, model: k.model })),
  ];
}

export async function completeAgentChat(
  messages: AgentChatMessage[],
  opts?: {
    keyId?: string;
    pendingMutateConfirm?: AgentPendingMutateConfirm | null;
    signal?: AbortSignal;
  },
): Promise<AgentChatCompleteResult> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), CHAT_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  opts?.signal?.addEventListener('abort', onAbort);
  try {
    const { ok, status, data } = await oroborosJson<{
      success?: boolean;
      content?: string;
      error?: string;
      message?: string;
      toolCallCount?: number;
    }>('/api/miniapp/mcp/chat', {
      method: 'POST',
      headers: oroborosAuthHeaders(),
      body: JSON.stringify(chatBody(messages, opts)),
      signal: controller.signal,
    });
    if (!ok || data?.success === false) {
      return {
        success: false,
        error: humanizeAgentApiError(data?.error || data?.message || `Chat failed (${status})`),
      };
    }
    return {
      success: true,
      content: String(data?.content || '').trim(),
      toolCallCount: data?.toolCallCount,
    };
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      return { success: false, error: 'Request cancelled' };
    }
    return { success: false, error: String(e?.message || 'Chat failed') };
  } finally {
    window.clearTimeout(timer);
    opts?.signal?.removeEventListener('abort', onAbort);
  }
}

function parseSseFrame(frame: string): { event: string; data: any } | null {
  const lines = frame.split(/\r?\n/);
  const event = lines.find((l) => l.startsWith('event:'))?.slice(6).trim() || 'message';
  const dataText = lines
    .filter((l) => l.startsWith('data:'))
    .map((l) => l.slice(5).trim())
    .join('\n')
    .trim();
  if (!dataText || dataText === '[DONE]') return null;
  try {
    return { event, data: JSON.parse(dataText) };
  } catch {
    return null;
  }
}

/** Stream a turn; falls back to non-streaming complete if the body is not readable. */
export async function streamAgentChat(
  messages: AgentChatMessage[],
  handlers: AgentChatStreamHandlers,
  opts?: {
    keyId?: string;
    pendingMutateConfirm?: AgentPendingMutateConfirm | null;
  },
): Promise<void> {
  const controller = new AbortController();
  let timedOut = false;
  let timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, CHAT_TIMEOUT_MS);
  const bumpIdle = () => {
    if (controller.signal.aborted) return;
    window.clearTimeout(timer);
    timedOut = false;
    timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, CHAT_TIMEOUT_MS);
  };
  const onAbort = () => controller.abort();
  handlers.signal?.addEventListener('abort', onAbort);

  let assembled = '';
  try {
    const response = await oroborosFetch('/api/miniapp/mcp/chat/stream', {
      method: 'POST',
      headers: oroborosAuthHeaders(),
      body: JSON.stringify(chatBody(messages, opts)),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errJson = await response.json().catch(() => null);
      const msg = humanizeAgentApiError(
        (errJson as any)?.error || (errJson as any)?.message || `Chat failed (${response.status})`,
      );
      handlers.onError?.(msg);
      return;
    }

    if (!response.body) {
      const fallback = await completeAgentChat(messages, {
        keyId: opts?.keyId,
        pendingMutateConfirm: opts?.pendingMutateConfirm,
        signal: controller.signal,
      });
      if (!fallback.success) {
        handlers.onError?.(fallback.error || 'Chat failed');
        return;
      }
      handlers.onDone?.(fallback.content || '');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let gotDone = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split(/\n\n/);
      buffer = parts.pop() || '';
      for (const frame of parts) {
        const parsed = parseSseFrame(frame);
        if (!parsed) continue;
        const { event, data } = parsed;
        if (event === 'heartbeat') {
          bumpIdle();
          continue;
        }
        bumpIdle();
        if (event === 'assistant_delta') {
          const delta = String(data?.delta || '');
          if (!delta) continue;
          assembled += delta;
          handlers.onDelta?.(delta);
          continue;
        }
        if (event === 'status' || event === 'tool_start') {
          const msg =
            event === 'tool_start'
              ? String(data?.activity || `Running ${data?.name || 'tool'}…`)
              : String(data?.message || 'Working…');
          handlers.onStatus?.(msg);
          continue;
        }
        if (event === 'tool_result') {
          const toolName = String(data?.name || 'tool');
          const result = (data?.result || {}) as Record<string, unknown>;
          const ok = result.ok !== false;
          handlers.onStatus?.(
            ok ? `Finished ${toolName}` : String(data?.userMessage || result.error || 'Tool failed'),
          );
          handlers.onToolResult?.({
            name: toolName,
            ok,
            requiresConfirmation: !!result.requiresConfirmation,
            confirmToken: String(result.confirmToken || '').trim() || undefined,
            confirmArgs: (data?.confirmAction?.args || {}) as Record<string, unknown>,
            summary: result.summary != null ? String(result.summary) : undefined,
            userMessage: data?.userMessage != null ? String(data.userMessage) : undefined,
            result,
          });
          continue;
        }
        if (event === 'error' || event === 'failure') {
          handlers.onError?.(humanizeAgentApiError(data?.message || data?.error || 'Chat failed'));
          gotDone = true;
          return;
        }
        if (event === 'done' || event === 'complete') {
          if (data?.failed) {
            handlers.onError?.(humanizeAgentApiError(data?.error || data?.content || 'Chat failed'));
            gotDone = true;
            return;
          }
          const content = String(data?.content || assembled || '').trim();
          assembled = content || assembled;
          handlers.onDone?.(content || assembled);
          gotDone = true;
          return;
        }
        if (typeof data?.content === 'string' && data.content && event === 'message') {
          assembled = String(data.content);
          handlers.onDone?.(assembled);
          gotDone = true;
          return;
        }
      }
    }

    if (!gotDone) {
      handlers.onDone?.(assembled.trim());
    }
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      handlers.onError?.(
        timedOut
          ? 'Request timed out. Try a shorter question or try again in a moment.'
          : 'Request cancelled',
      );
      return;
    }
    handlers.onError?.(String(e?.message || 'Chat failed'));
  } finally {
    window.clearTimeout(timer);
    handlers.signal?.removeEventListener('abort', onAbort);
  }
}

export type AgentConversationSyncThread = {
  id: string;
  title: string;
  messages: Array<{ role: string; content: string; chart?: Record<string, unknown> | null }>;
  chartSession?: Record<string, unknown> | null;
  createdAt: number;
  updatedAt: number;
};

export async function syncAgentConversations(opts: {
  threads: AgentConversationSyncThread[];
  deletedThreadIds?: string[];
  activeThreadId?: string;
}): Promise<{
  ok: boolean;
  storage?: string;
  threads?: AgentConversationSyncThread[];
  error?: string;
}> {
  const res = await oroborosJson<{
    success?: boolean;
    storage?: string;
    threads?: AgentConversationSyncThread[];
    error?: string;
  }>('/api/miniapp/mcp/conversations/sync', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      threads: opts.threads,
      deletedThreadIds: opts.deletedThreadIds || [],
      activeThreadId: opts.activeThreadId,
    }),
  });
  if (!res.ok || !res.data?.success) {
    return {
      ok: false,
      error: humanizeAgentApiError(res.data?.error || res.error || 'Could not sync conversations'),
    };
  }
  return {
    ok: true,
    storage: res.data.storage,
    threads: Array.isArray(res.data.threads) ? res.data.threads : [],
  };
}

export type AgentConversationShareLink = {
  shareUrl: string;
  miniAppShareUrl?: string;
  telegramShareUrl?: string;
  title?: string;
  messageCount?: number;
  formattedText?: string;
};

export type AgentSharedConversationPreview = {
  title: string;
  messageCount: number;
  messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>;
  formattedText: string;
};

export async function shareAgentConversation(opts: {
  title?: string;
  messages: Array<{ role: string; content: string }>;
}): Promise<{ ok: boolean; data?: AgentConversationShareLink; error?: string }> {
  const res = await oroborosJson<{
    success?: boolean;
    data?: AgentConversationShareLink;
    error?: string;
  }>('/api/miniapp/mcp/conversations/share', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({
      title: opts.title || 'Agent chat',
      messages: opts.messages,
    }),
  });
  if (!res.ok || !res.data?.success || !res.data.data?.shareUrl) {
    return { ok: false, error: humanizeAgentApiError(res.data?.error || res.error || 'Could not create share link') };
  }
  return { ok: true, data: res.data.data };
}

export async function previewSharedAgentConversation(
  token: string,
): Promise<{ ok: boolean; preview?: AgentSharedConversationPreview; error?: string }> {
  const res = await oroborosJson<{
    success?: boolean;
    preview?: AgentSharedConversationPreview;
    error?: string;
  }>('/api/miniapp/mcp/conversations/share/preview', {
    method: 'POST',
    headers: oroborosAuthHeaders(),
    body: JSON.stringify({ token }),
  });
  if (!res.ok || !res.data?.success || !res.data.preview) {
    return {
      ok: false,
      error: humanizeAgentApiError(res.data?.error || res.error || 'Shared conversation not found'),
    };
  }
  return { ok: true, preview: res.data.preview };
}
